import React, { Suspense, lazy } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { Toaster } from "sonner";

import LoginPage from "@/pages/Login";
import AdminLayout from "@/pages/admin/AdminLayout";
import AgentLayout from "@/pages/agent/AgentLayout";

// Code-split every route so a page's JS/CSS is fetched only when the user
// navigates there. Cuts first-paint bundle size dramatically and makes each
// route load in parallel with the API call for that page.
const AdminDashboard   = lazy(() => import("@/pages/admin/Dashboard"));
const AdminLeads       = lazy(() => import("@/pages/admin/Leads"));
const Team             = lazy(() => import("@/pages/admin/Team"));
const Templates        = lazy(() => import("@/pages/admin/Templates"));
const FollowupsAdmin   = lazy(() => import("@/pages/admin/FollowupsAdmin"));
const NotificationsPage = lazy(() => import("@/pages/admin/NotificationsPage"));
const StaffActivity    = lazy(() => import("@/pages/admin/StaffActivity"));
const AdminOrders      = lazy(() => import("@/pages/admin/AdminOrders"));
const Expenses         = lazy(() => import("@/pages/admin/Expenses"));
const PnLReport        = lazy(() => import("@/pages/admin/PnLReport"));
const Targets          = lazy(() => import("@/pages/admin/Targets"));
const TargetsLeaderboard = lazy(() => import("@/pages/admin/TargetsLeaderboard"));
const AIManagerPage    = lazy(() => import("@/pages/admin/AIManager"));

const AgentHome         = lazy(() => import("@/pages/agent/AgentHome"));
const AgentLeads        = lazy(() => import("@/pages/agent/AgentLeads"));
const AgentLeadPage     = lazy(() => import("@/pages/agent/AgentLeadPage"));
const AgentFollowups    = lazy(() => import("@/pages/agent/AgentFollowups"));
const AgentNotifications = lazy(() => import("@/pages/agent/AgentNotifications"));
const AgentOrders       = lazy(() => import("@/pages/agent/AgentOrders"));

// Minimal fallback — no big spinner card because it flashes on fast networks;
// a subtle bar keeps the layout stable while the chunk loads.
function RouteFallback() {
  return (
    <div className="pointer-events-none fixed top-0 left-0 h-0.5 w-full overflow-hidden">
      <div className="h-full w-1/3 animate-[loading_1s_ease-in-out_infinite] bg-primary" />
      <style>{`@keyframes loading{0%{margin-left:-33%}100%{margin-left:100%}}`}</style>
    </div>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <Suspense fallback={<RouteFallback />}>
            <Routes>
              <Route path="/" element={<Navigate to="/login" replace />} />
              <Route path="/login" element={<LoginPage />} />

              {/* Admin */}
              <Route
                path="/admin"
                element={
                  <ProtectedRoute role="admin">
                    <AdminLayout />
                  </ProtectedRoute>
                }
              >
                <Route index element={<AdminDashboard />} />
                <Route path="leads" element={<AdminLeads />} />
                <Route path="followups" element={<FollowupsAdmin />} />
                <Route path="team" element={<Team />} />
                <Route path="activity" element={<StaffActivity />} />
                <Route path="templates" element={<Templates />} />
                <Route path="notifications" element={<NotificationsPage />} />
                <Route path="orders" element={<AdminOrders />} />
                <Route path="expenses" element={<Expenses />} />
                <Route path="pnl" element={<PnLReport />} />
                <Route path="targets" element={<Targets />} />
                <Route path="leaderboard" element={<TargetsLeaderboard />} />
                <Route path="leads/:id" element={<AdminLeads />} />
                <Route path="ai" element={<AIManagerPage />} />
              </Route>

              {/* Agent */}
              <Route
                path="/app"
                element={
                  <ProtectedRoute role="agent">
                    <AgentLayout />
                  </ProtectedRoute>
                }
              >
                <Route index element={<AgentHome />} />
                <Route path="leads" element={<AgentLeads />} />
                <Route path="leads/:id" element={<AgentLeadPage />} />
                <Route path="followups" element={<AgentFollowups />} />
                <Route path="notifications" element={<AgentNotifications />} />
                <Route path="orders" element={<AgentOrders />} />
                <Route path="leaderboard" element={<TargetsLeaderboard />} />
              </Route>

              <Route path="*" element={<Navigate to="/login" replace />} />
            </Routes>
          </Suspense>
          <Toaster
            position="top-center"
            richColors
            closeButton
            toastOptions={{ className: "font-medium" }}
          />
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
